import {
  ArrowRightOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  RocketOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Button, message } from 'antd';
import { setLoginAction } from 'common/commonRedux/auth/actions';
import { useIsDark } from 'common/hooks';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import topImg from '../authTop.png';
import logo from '../otpVerificaton/logo.png';
import background from './signup_bg.png';

const SignUpSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: any = useDispatch();
  const isDark = useIsDark();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const signupContext = useSelector(
    (state: any) => state.auth?.profileData?.signupContext
  ) || {};

  const { email, password } = (location.state as { email?: string; password?: string }) || {};

  const planName = signupContext.planName || 'Professional';
  const isTrial = !!signupContext.isTrial;
  const trialDays = signupContext.trialDays || 15;

  const handleGetStarted = () => {
    if (!email || !password) {
      navigate('/login');
      return;
    }
    setIsLoggingIn(true);
    dispatch(setLoginAction({ email, password, navigate }))
      .catch(() => {
        message.error('Auto sign-in failed. Please log in manually.');
        navigate('/login');
      })
      .finally(() => {
        setIsLoggingIn(false);
      });
  };

  const checklist = [
    'Your organization workspace is live',
    `${planName} plan${isTrial ? ` · ${trialDays}-day free trial` : ''} activated`,
    'Admin account secured and ready to use',
  ];

  const nextSteps = [
    {
      icon: <TeamOutlined className="text-primaryColor text-xl" />,
      title: 'Invite your team',
      desc: 'Send invites and let employees self-register in minutes.',
    },
    {
      icon: <ClockCircleOutlined className="text-primaryColor text-xl" />,
      title: 'Start time tracking',
      desc: 'Configure shifts, attendance rules, and projects.',
    },
    {
      icon: <RocketOutlined className="text-primaryColor text-xl" />,
      title: 'Explore insights',
      desc: 'View productivity reports and team performance from day one.',
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 w-full min-h-screen">
      <div className={`w-full h-full flex justify-center items-center px-4 md:px-12 py-12 relative overflow-hidden ${isDark ? 'bg-[#091320]' : 'bg-[#DEEDFF]'}`}>
        <img src={topImg} alt="" className={`absolute top-0 left-0 ${isDark ? 'opacity-30' : ''}`} />
        <div className="relative z-10 w-full md:max-w-lg flex flex-col gap-6">
          <img src={logo} alt="Trackforce Logo" className="w-[120px] object-contain" />

          <div className="flex items-center gap-3">
            <CheckCircleFilled className={`text-3xl ${isDark ? 'text-emerald-400' : 'text-[#22c55e]'}`} />
            <h1 className={`text-2xl md:text-3xl font-semibold ${isDark ? 'text-white' : 'text-[#0A1629]'}`}>
              You’re all set!
            </h1>
          </div>

          <p className={`text-base ${isDark ? 'text-[#94A3B8]' : 'text-[#475569]'}`}>
            Your business has been successfully set up on Trackforce. Here’s a
            quick summary of what just happened:
          </p>

          <div className={`rounded-xl p-5 shadow-sm border flex flex-col gap-3 ${isDark ? 'bg-[#0B172B] border-[#2E3138]' : 'bg-white border-[#E2E8F0]'}`}>
            {checklist.map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircleFilled className={`text-lg mt-0.5 ${isDark ? 'text-emerald-400' : 'text-[#22c55e]'}`} />
                <span className={`text-sm ${isDark ? 'text-[#CBD5E1]' : 'text-[#334155]'}`}>{item}</span>
              </div>
            ))}
          </div>

          {email && (
            <div className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-[#475569]'}`}>
              Signed in as <span className={`font-semibold ${isDark ? 'text-white' : 'text-[#0A1629]'}`}>{email}</span>
            </div>
          )}

          <Button
            type="primary"
            size="large"
            loading={isLoggingIn}
            className="w-full !text-white font-medium h-12 rounded-xl"
            onClick={handleGetStarted}
          >
            {isLoggingIn ? 'Signing you in...' : 'Take me to my dashboard'}
            <ArrowRightOutlined />
          </Button>
        </div>
      </div>

      <div className="h-full py-12 relative bg-[#0f172a] hidden lg:flex flex-col justify-center items-center">
        <div className="absolute inset-0 z-0 opacity-40">
          <img src={background} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 w-full max-w-lg px-10 text-white">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-primaryColor/90 bg-white/50 px-3 py-1 rounded-full">
            What’s next
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold mt-4 leading-tight">
            Three quick steps to make Trackforce yours.
          </h2>
          <p className="mt-3 text-sm text-gray-300">
            We’ve prepared everything you need to launch your team. Pick up
            where you left off, right from the dashboard.
          </p>

          <div className="mt-8 flex flex-col gap-4">
            {nextSteps.map((step) => (
              <div
                key={step.title}
                className="flex items-start gap-4 bg-white/5 border border-white/20 rounded-xl p-4 backdrop-blur-sm"
              >
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  {step.icon}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">{step.title}</h3>
                  <p className="text-xs text-gray-300 mt-1">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-xs text-gray-400">
            Need a hand? Reach us anytime at{' '}
            <span className="text-white font-medium">support@trackforce.io</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUpSuccess;
